import { useState } from 'react'

import * as S from './ApprovalCard.styles'
import type { ApprovalCardState } from './ApprovalCard.styles'

// Mirrors the `data-remy-approval` part the Remy web gateway streams when a
// guarded MCP tool call pauses for human approval.
export interface RemyApprovalData {
  approvalId: string
  expiresAt: string
  actions: Array<{
    name: string
    remoteName: string
    server?: string
    risk?: string
    guidance?: string
    argsJson: string
  }>
}

export type ApprovalDecision = 'approve' | 'reject'

interface ApprovalCardProps {
  approval: RemyApprovalData
  // Decision already taken in this mount (state lives in RemyChat so it
  // survives card re-renders within the session).
  decided?: ApprovalDecision
  // Disable buttons while any turn is streaming.
  busy: boolean
  onDecide: (approvalId: string, decision: ApprovalDecision) => void
}

export const ApprovalCard = ({ approval, decided, busy, onDecide }: ApprovalCardProps) => {
  // Expiry only matters while undecided; compute once per render on demand
  // instead of a ticking timer — the buttons re-check on click anyway.
  const [expiredAtRender] = useState(() => Date.now() >= Date.parse(approval.expiresAt))
  const expired = !decided && (expiredAtRender || Date.now() >= Date.parse(approval.expiresAt))
  const state: ApprovalCardState = decided
    ? decided === 'approve'
      ? 'approved'
      : 'rejected'
    : expired
      ? 'expired'
      : 'pending'

  return (
    <S.Card data-testid="remy-approval-card" $state={state}>
      {approval.actions.map(action => (
        <S.Action key={action.name}>
          <S.Headline>
            Remy needs your approval to run tool <S.ToolName>{action.remoteName}</S.ToolName>
          </S.Headline>
          <S.Facts>
            {action.risk && (
              <div>
                Risk: <strong>{action.risk}</strong>
              </div>
            )}
            {action.server && (
              <div>
                Server: <strong>{action.server}</strong>
              </div>
            )}
            {action.guidance && (
              <div>
                Guidance: <strong>{action.guidance}</strong>
              </div>
            )}
          </S.Facts>
          <S.ArgsDetails>
            <S.ArgsSummary>Arguments</S.ArgsSummary>
            <S.Args>{action.argsJson}</S.Args>
          </S.ArgsDetails>
        </S.Action>
      ))}
      {decided ? (
        <S.ResolvedNote>{decided === 'approve' ? '✅ Approved' : '❌ Rejected'}</S.ResolvedNote>
      ) : expired ? (
        <S.ResolvedNote>This approval request expired — ask Remy again.</S.ResolvedNote>
      ) : (
        <S.ButtonRow>
          <S.ApproveButton
            type="button"
            disabled={busy}
            onClick={() => onDecide(approval.approvalId, 'approve')}
          >
            Approve
          </S.ApproveButton>
          <S.RejectButton
            type="button"
            disabled={busy}
            onClick={() => onDecide(approval.approvalId, 'reject')}
          >
            Reject
          </S.RejectButton>
        </S.ButtonRow>
      )}
    </S.Card>
  )
}
